/**
 * E-Link (E-Link Page / Template) domain models.
 *
 * Mirrors the React `services/ELink` feature. The backend returns snake_case;
 * these interfaces describe the camelCase shape after `transformSnakeToCamel`
 * is applied at the service boundary.
 *
 * The React `BE_FE_MAP.getAll` mapping is the source of truth for the list-row
 * shape:
 *   name              -> title
 *   is_default        -> isDefault
 *   short_url         -> shortUrl
 *   long_url          -> longUrl
 *   thumbnail_logo.value      -> imageUrl
 *   thumbnail_logo.is_enabled -> isImageEnabled
 */

/** A thumbnail logo setting as returned by the list endpoint. */
export interface ELinkThumbnailLogo {
  value?: string | null;
  isEnabled?: boolean;
}

/** A single E-Link page as shown on the admin list (card grid). */
export interface ELink {
  uuid: string;
  /** Backend `name`; the display title of the E-Link page. */
  title: string;
  description?: string | null;
  slug?: string | null;
  isDefault?: boolean;
  shortUrl?: string | null;
  longUrl?: string | null;
  /** Thumbnail image URL (backend `thumbnail_logo.value`). */
  imageUrl?: string | null;
  /** Whether the thumbnail image is enabled (backend `thumbnail_logo.is_enabled`). */
  isImageEnabled?: boolean;
}

export interface ELinksListMeta {
  currentPage: number;
  perPage: number;
  total?: number;
  /** Backend `last_page`; the number of the last page. */
  totalPages?: number;
}

export interface ELinksListResponse {
  data: ELink[];
  meta: ELinksListMeta;
}

export interface ELinksListParams {
  page?: number;
  perPage?: number;
  search?: string;
  sort?: string;
  sortDirection?: string;
}

export const DEFAULT_E_LINKS_CURRENT_PAGE = 1;
export const DEFAULT_E_LINKS_PER_PAGE = 24;
export const E_LINKS_PAGE_SIZE_OPTIONS = [DEFAULT_E_LINKS_PER_PAGE, 48, 96] as const;

/** Default sort applied by the React list page (hard-coded to created_at desc). */
export const DEFAULT_E_LINKS_SORT = 'created_at';
export const DEFAULT_E_LINKS_SORT_DIRECTION = 'desc';

/* -------------------------------------------------------------------------- */
/* Editor (create/edit) models                                                 */
/* -------------------------------------------------------------------------- */

/** A single option consumed by a Kendo dropdown/multiselect (valuePrimitive). */
export interface ELinkSelectOption {
  label: string;
  value: string;
}

/** Redirect type options (React `redirectTypes`: 301 permanent / 307 temporary). */
export const REDIRECT_TYPE_OPTIONS: ELinkSelectOption[] = [
  { label: '301 - Permanent Redirect', value: '301' },
  { label: '307 - Temporary Redirect', value: '307' },
];

/** Hidden-field options (React `HIDDEN_FIELDS`). */
export const HIDDEN_FIELD_OPTIONS: ELinkSelectOption[] = [
  { label: 'Description', value: 'description' },
];

/**
 * Options loaded for the editor dropdowns. Mirrors the React
 * `internalFieldOptionsSelector` (domains, link templates, qr templates) plus
 * the static redirect-type/hidden-field lists resolved in the component.
 */
export interface ELinkManageOptions {
  domains: ELinkSelectOption[];
  linkTemplates: ELinkSelectOption[];
  qrTemplates: ELinkSelectOption[];
}

/**
 * A single API setting entry. On the wire the backend uses snake_case
 * (`is_enabled`); this is the camelCase shape. `name` is the numeric setting
 * code (1..24) as returned/sent.
 */
export interface ELinkApiSetting {
  name: number | string;
  uuid?: string;
  value: string;
  isEnabled?: boolean;
}

/**
 * The full detail payload used by the editor (edit/clone mode). After the
 * service normalises the response, `settings` is a map keyed by the numeric
 * setting code, and the option arrays are flattened to arrays of uuid strings.
 */
export interface ELinkDetail {
  uuid?: string;
  name?: string;
  description?: string | null;
  slug?: string | null;
  isDefault?: boolean;
  isQRCodeEnabled?: boolean;
  isTrustedDestinationEnabled?: boolean;
  isShortUrlEditable?: boolean;
  isProtected?: boolean;
  isPublished?: boolean | string | null;
  isUseExistingLink?: boolean;
  redirectType?: string;
  hiddenFields?: string[];
  domains?: string[];
  linkTemplates?: string[];
  qrTemplates?: string[];
  /** Settings keyed by numeric code, e.g. `{ 1: { value, isEnabled, uuid } }`. */
  settings?: Record<string, ELinkApiSetting>;
}

/**
 * Payload sent to create/update an E-Link (camelCase; the service maps it to
 * snake_case). Mirrors the React `FE_BE_MAP.createTemplate` contract.
 */
export interface ELinkSavePayload {
  name: string;
  description?: string;
  slug: string;
  redirectType: string;
  isQRCodeEnabled: boolean;
  isTrustedDestinationEnabled?: boolean;
  isShortUrlEditable: boolean;
  isProtected: boolean;
  isUseExistingLink: boolean;
  hiddenFields: string[];
  /** Wrapped as `[{ uuid }]` at the service boundary. */
  domains: string[];
  linkTemplates: string[];
  qrTemplates: string[];
  /** Full settings array (both enabled and disabled entries are persisted). */
  settings: ELinkApiSetting[];
}

/* -------------------------------------------------------------------------- */
/* External (public) page models                                               */
/* -------------------------------------------------------------------------- */

/** A domain option on the public page (carries id for TD checks). */
export interface ELinkExternalDomain {
  id?: string | number;
  label: string;
  value: string;
  isTrustedEnabled?: boolean;
}

/** A link-template option on the public page. */
export interface ELinkExternalTemplate {
  label: string;
  value: string;
}

/**
 * The public page payload returned by GET /external/elink-pages/:slug.
 * `settings` is a map keyed by the numeric setting code (as with the editor).
 */
export interface ELinkExternalPage {
  uuid: string;
  name?: string;
  slug?: string;
  isQRCodeEnabled?: boolean;
  isTrustedDestinationEnabled?: boolean;
  isShortUrlEditable?: boolean;
  redirectType?: { value?: string; label?: string } | string;
  hiddenFields?: string[];
  domains: ELinkExternalDomain[];
  linkTemplates: ELinkExternalTemplate[];
  qrTemplates: ELinkExternalTemplate[];
  settings: Record<string, ELinkApiSetting>;
}

/** A single attribute assigned to a link template on the public page. */
export interface ELinkTemplateAttribute {
  uuid: string;
  code: string;
  name: string;
  description?: string;
  dataType?: string;
  fieldType?: string;
  defaultValue?: string;
  type?: string;
  isRequiredTemplate?: boolean;
}

/** Link-template detail fetched when the user selects a template. */
export interface ELinkTemplateDetail {
  name?: string;
  redirectType?: string;
  additionalParameterSchema?: unknown;
  slugPrefix?: string;
  assignedAttributes: ELinkTemplateAttribute[];
}

/** Payload for creating a link from the public page. */
export interface ELinkCreateLinkPayload {
  uuid: string;
  domain: string;
  template?: string;
  longUrl: string;
  slug: string;
  description?: string;
  redirectType?: string;
  additionalParameterSchema?: unknown;
  attributes?: Record<string, unknown>;
}

/** Result of creating a link (or matching an existing one). */
export interface ELinkCreatedLink {
  shortURL: string;
  domain?: { name?: string; ssl?: boolean };
  alias?: string;
  isExistedLink?: boolean;
}
